import { useHoras, useNomina, useSubcontratos } from '@/hooks/useManoObra';
import { calcularManoObra } from '@/lib/calcularManoObra';
import { cliente } from '@/lib/cliente';
import type { FiltroVenta } from '@/lib/filtroVenta';
import type { SemanaOperativa } from '@adelante/shared';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface FilaResumenMes {
  semana: SemanaOperativa;
  cargando: boolean;
  baseSemanal: boolean;
  cronoEsp: number;
  cronoReal: number;
  costoEsp: number;
  costoReal: number;
  // Financiero — producido en la semana (₡).
  directo: number;
  indirecto: number;
  venta: number;
  utilidad: number;
}

export interface ResumenMes {
  filas: FilaResumenMes[];
  tot: { cronoEsp: number; cronoReal: number; costoEsp: number; costoReal: number };
  /** Totales financieros acumulados del mes a la fecha (Σ semanales con base). */
  totFin: { directo: number; indirecto: number; venta: number; utilidad: number };
  difCrono: number;
  difCosto: number;
  cargando: boolean;
}

/**
 * Resumen del Mes: por cada semana operativa del mismo mes que la seleccionada,
 * trae los promedios Crono/Costo (esperado vs real) del reporte semanal +
 * acumulado y diferencia. Comparte caché con el reporte abierto (misma queryKey).
 */
export function useResumenMes(
  semanas: SemanaOperativa[],
  semanaSel: number | null,
  filtroVenta: FiltroVenta,
): ResumenMes {
  const delMes = useMemo(() => {
    const sel = semanas.find((s) => Number(s.id) === semanaSel);
    if (!sel) return [];
    const mes = sel.fecha_inicio.slice(0, 7); // YYYY-MM
    return semanas
      .filter((s) => s.fecha_inicio.slice(0, 7) === mes)
      .sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio));
  }, [semanas, semanaSel]);

  const resultados = useQueries({
    queries: delMes.map((s) => ({
      queryKey: ['reporte-semanal', Number(s.id), filtroVenta],
      queryFn: () => cliente.reportes.semanal(Number(s.id), filtroVenta),
      staleTime: 30_000,
    })),
  });

  return useMemo(() => {
    const filas: FilaResumenMes[] = delMes.map((s, i) => {
      const rep = resultados[i]?.data?.data ?? null;
      const t = rep?.totales;
      return {
        semana: s,
        cargando: resultados[i]?.isLoading ?? true,
        baseSemanal: rep?.base_semanal ?? false,
        cronoEsp: t?.crono_esperado_prom ?? 0,
        cronoReal: t?.crono_real_prom ?? 0,
        costoEsp: t?.costo_esperado_prom ?? 0,
        costoReal: t?.costo_real_prom ?? 0,
        directo: t?.monto_semana ?? 0,
        indirecto: t?.indirecto_semana ?? 0,
        venta: t?.venta_semana ?? 0,
        utilidad: t?.utilidad_semana ?? 0,
      };
    });
    const tot = filas.reduce(
      (a, f) => ({
        cronoEsp: a.cronoEsp + f.cronoEsp,
        cronoReal: a.cronoReal + (f.baseSemanal ? f.cronoReal : 0),
        costoEsp: a.costoEsp + f.costoEsp,
        costoReal: a.costoReal + (f.baseSemanal ? f.costoReal : 0),
      }),
      { cronoEsp: 0, cronoReal: 0, costoEsp: 0, costoReal: 0 },
    );
    // Financiero acumulado del mes = Σ de lo producido cada semana (solo las que
    // tienen base para el cálculo semanal).
    const totFin = filas.reduce(
      (a, f) => ({
        directo: a.directo + (f.baseSemanal ? f.directo : 0),
        indirecto: a.indirecto + (f.baseSemanal ? f.indirecto : 0),
        venta: a.venta + (f.baseSemanal ? f.venta : 0),
        utilidad: a.utilidad + (f.baseSemanal ? f.utilidad : 0),
      }),
      { directo: 0, indirecto: 0, venta: 0, utilidad: 0 },
    );
    return {
      filas,
      tot,
      totFin,
      difCrono: tot.cronoReal - tot.cronoEsp,
      difCosto: tot.costoReal - tot.costoEsp,
      cargando: filas.some((f) => f.cargando),
    };
  }, [delMes, resultados]);
}

export interface FilaResumenMesMO {
  semana: SemanaOperativa;
  cargando: boolean;
  baseSemanal: boolean;
  /** ₡ MO total gastada de la semana (nómina directa prorrateada + subcontratos). */
  moGastada: number;
  /** m² construidos en la semana. */
  m2: number;
  /** ₡ costo de MO por m² = moGastada / m². */
  costoM2: number;
}

export interface ResumenMesMO {
  filas: FilaResumenMesMO[];
  tot: { moGastada: number; m2: number; costoM2: number };
  cargando: boolean;
}

/**
 * Resumen del Mes — Mano de Obra: por cada semana operativa del mismo mes que la
 * seleccionada, calcula MO total gastada, m² construidos y costo MO/m² (reusando
 * calcularManoObra con la nómina/horas/subcontratos globales). Comparte la caché
 * de reportes con useResumenMes (misma queryKey).
 */
export function useResumenMesMO(
  semanas: SemanaOperativa[],
  semanaSel: number | null,
  filtroVenta: FiltroVenta,
  // Carga bajo demanda: el reporte semanal es pesado y traer todas las semanas
  // del mes en paralelo satura el pool. Solo se consulta cuando `enabled` (la
  // tabla está abierta). Si ya está en caché (otras vistas del mes), es instantáneo.
  enabled = true,
): ResumenMesMO {
  const delMes = useMemo(() => {
    const sel = semanas.find((s) => Number(s.id) === semanaSel);
    if (!sel) return [];
    const mes = sel.fecha_inicio.slice(0, 7);
    return semanas
      .filter((s) => s.fecha_inicio.slice(0, 7) === mes)
      .sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio));
  }, [semanas, semanaSel]);

  const resultados = useQueries({
    queries: delMes.map((s) => ({
      queryKey: ['reporte-semanal', Number(s.id), filtroVenta],
      queryFn: () => cliente.reportes.semanal(Number(s.id), filtroVenta),
      staleTime: 30_000,
      enabled,
    })),
  });

  const { data: nominaD } = useNomina();
  const { data: horasD } = useHoras();
  const { data: subcD } = useSubcontratos();

  return useMemo(() => {
    const filas: FilaResumenMesMO[] = delMes.map((s, i) => {
      const rep = resultados[i]?.data?.data ?? null;
      const calc = rep
        ? calcularManoObra(
            Number(s.id),
            rep,
            nominaD?.data ?? [],
            horasD?.data ?? [],
            subcD?.data ?? [],
          )
        : null;
      return {
        semana: s,
        cargando: resultados[i]?.isLoading ?? true,
        baseSemanal: rep?.base_semanal ?? false,
        moGastada: calc?.moTotalGastada ?? 0,
        m2: calc?.m2Sem ?? 0,
        costoM2: calc?.costoMOm2 ?? 0,
      };
    });
    // Totales: solo semanas con base (m² del cierre calculable). Costo/m² del
    // total = Σ MO gastada / Σ m² (ponderado, no promedio simple).
    const moGastada = filas.reduce((a, f) => a + (f.baseSemanal ? f.moGastada : 0), 0);
    const m2 = filas.reduce((a, f) => a + (f.baseSemanal ? f.m2 : 0), 0);
    return {
      filas,
      tot: { moGastada, m2, costoM2: m2 > 0 ? moGastada / m2 : 0 },
      cargando: filas.some((f) => f.cargando),
    };
  }, [delMes, resultados, nominaD, horasD, subcD]);
}
